<template>
  <vue-flickity
    ref="carousel"
    :options="options"
  >
    <slot />
  </vue-flickity>
</template>

<script>

// Re-measures Flickity shortly after mount and after every slide change, so content that grows late
// (a webfont swapping in and reflowing copy to an extra line) cannot be left clipped by the height
// Flickity pinned on init.
//
// The reload window is deliberately spread across 100-400ms rather than run once: the exact moment
// the font lands is not knowable, so it is sampled a few times instead.
const RELOAD_DELAYS = [100, 200, 300, 400]

export default {
  props: {
    options: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      activeIndex: 0,
      reloadTimers: []
    }
  },
  computed: {
  },
  mounted () {
    this.scheduleReloads()

    // Bind the change hook only once Flickity is actually up — see readyCarousel().
    const bind = () => {
      const carousel = this.readyCarousel()
      if (!carousel) return false
      carousel.on('change', () => { this.scheduleReloads() })
      return true
    }

    if (!bind()) {
      RELOAD_DELAYS.forEach((delay) => {
        this.reloadTimers.push(setTimeout(bind, delay))
      })
    }
  },
  beforeUnmount () {
    this.clearReloads()
  },
  methods: {
    /**
     * The vue-flickity wrapper proxies every method straight onto its internal Flickity instance
     * without a guard (node_modules/vue-flickity/src/flickity.vue:56 and siblings), so a live ref is
     * not enough — calling reloadCells() before init(), or while the patched rerender() has torn the
     * instance down, throws "Cannot read properties of undefined (reading 'reloadCells')" and takes
     * other page scripts with it. flickity() is the wrapper's own accessor for that instance.
     *
     * @return {object|null} the wrapper, only when its Flickity instance is live
     */
    readyCarousel () {
      const carousel = this.$refs.carousel
      if (!carousel || typeof carousel.flickity !== 'function') return null
      return carousel.flickity() ? carousel : null
    },

    scheduleReloads () {
      RELOAD_DELAYS.forEach((delay) => {
        // Re-checked inside the timer rather than captured outside it: a hidden or re-rendered mount
        // can be torn down between scheduling and firing.
        this.reloadTimers.push(setTimeout(() => { this.readyCarousel()?.reloadCells() }, delay))
      })
    },
    clearReloads () {
      this.reloadTimers.forEach(clearTimeout)
      this.reloadTimers = []
    }
  }
}
</script>
